// The DM's screen and the campaign's notes at the table (issue 1153), the client's half. Both live on the site; the
// server reads them with the DM's own bearer, so every call here is to THIS origin (`requestJson`, so `authFetch`).
// Nothing of them ever arrives over the socket.
import { requestJson } from './api';
import { siteUrl } from './session';

/** One pinned tile: its address, its name (null when the site no longer has it), its page and its compact facts. */
export interface DmTile { id: string; segment: string; slug: string; name: string | null; url: string; facts: Array<{ label: string; value: string }>; rollable: boolean }
export type NoteKind = 'note' | 'quest' | 'relationship';
export interface CampaignNote { id: string; kind: NoteKind; title: string; body: string; status: 'open' | 'done' | null; position: number }

type Refused = { kind: 'refused'; reason: string } | { kind: 'error' };
export type ScreenAnswer = { kind: 'ok'; system: string; tiles: DmTile[] } | Refused;
export type NotesAnswer = { kind: 'ok'; campaignId: string; notes: CampaignNote[] } | Refused;
export type QuestMarked = { kind: 'ok'; note: CampaignNote } | Refused;

const enc = encodeURIComponent;
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isTile = (v: unknown): v is DmTile => isObj(v) && typeof v.id === 'string' && typeof v.segment === 'string' && typeof v.slug === 'string'
  && (v.name === null || typeof v.name === 'string') && typeof v.url === 'string' && v.url.startsWith('/') && Array.isArray(v.facts) && typeof v.rollable === 'boolean';
const isNote = (v: unknown): v is CampaignNote => isObj(v) && typeof v.id === 'string' && (v.kind === 'note' || v.kind === 'quest' || v.kind === 'relationship')
  && typeof v.title === 'string' && typeof v.body === 'string' && (v.status === null || v.status === 'open' || v.status === 'done') && typeof v.position === 'number';
const refusedOr = (body: unknown): Refused => {
  const reason = (body as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' ? { kind: 'refused', reason } : { kind: 'error' };
};

/** The DM's pinned tiles for the table's game system. */
export async function readDmScreen(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<ScreenAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/dm-screen`, {}, fetchImpl);
  const b = (body ?? {}) as { system?: unknown; tiles?: unknown };
  if (status === 200 && typeof b.system === 'string' && Array.isArray(b.tiles)) {
    return { kind: 'ok', system: b.system, tiles: b.tiles.filter(isTile).map((t) => ({ ...t, facts: t.facts.filter((f) => isObj(f) && typeof f.label === 'string' && typeof f.value === 'string') })) };
  }
  return refusedOr(body);
}

/** The campaign's notes, quests and relationships. */
export async function readCampaignNotes(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<NotesAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/campaign-notes`, {}, fetchImpl);
  const b = (body ?? {}) as { campaignId?: unknown; notes?: unknown };
  if (status === 200 && typeof b.campaignId === 'string' && Array.isArray(b.notes)) return { kind: 'ok', campaignId: b.campaignId, notes: b.notes.filter(isNote) };
  return refusedOr(body);
}

/** Mark a quest open or done; the site keeps it. */
export async function markQuest(campaignId: string, noteId: string, status: 'open' | 'done', fetchImpl: typeof fetch = fetch): Promise<QuestMarked> {
  const { status: code, body } = await requestJson(`/api/tables/${enc(campaignId)}/campaign-notes/${enc(noteId)}/status`, { method: 'PUT', body: JSON.stringify({ status }) }, fetchImpl);
  const note = ((body ?? {}) as { note?: unknown }).note;
  if (code === 200 && isNote(note)) return { kind: 'ok', note };
  return refusedOr(body);
}

/** A kind's notes in the site's order; quests open first, then done, each in their order. */
export function notesOf(notes: CampaignNote[], kind: NoteKind): CampaignNote[] {
  const rank = (n: CampaignNote) => (n.status === 'done' ? 1 : 0);
  return notes.filter((n) => n.kind === kind).sort((a, b) => rank(a) - rank(b) || a.position - b.position);
}

/** The site's DM screen page for the game system, where tiles are pinned and arranged. */
export const dmScreenPageUrl = (system: string): string => `${siteUrl()}/dm-screen?system=${enc(system)}`;

// ── Pinning from the Compendium and the DM's own tile facts (qa-runs 407) ──
// Written on the site through this origin, with the DM's own sign-in, as Give writes a sheet.

/** A window event: the DM's screen changed here (a pin, an unpin, a kind's facts), so whatever shows it reads again. */
export const DM_SCREEN_CHANGED = 'tdt-dm-screen-changed';

export type PinAnswer = { kind: 'ok'; tile: { id: string; segment: string; slug: string } } | Refused;
export type DoneAnswer = { kind: 'ok' } | Refused;
/** What a kind's tiles may show, what the system shows, and the DM's own choice (null: the system's). */
export interface TileFactChoice { segment: string; family: string; available: Array<{ key: string; label: string }>; system: string[]; mine: string[] | null; max: number }
export type FactsAnswer = { kind: 'ok'; choice: TileFactChoice } | Refused;
export type FactsSaved = { kind: 'ok'; mine: string[] | null } | Refused;

const isKeys = (v: unknown): v is string[] => Array.isArray(v) && v.every((k) => typeof k === 'string');

/** Pin an entry to the DM's screen for the table's game system (already pinned is fine). */
export async function pinEntry(campaignId: string, segment: string, slug: string, fetchImpl: typeof fetch = fetch): Promise<PinAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/dm-screen`, { method: 'POST', body: JSON.stringify({ segment, slug }) }, fetchImpl);
  const tile = ((body ?? {}) as { tile?: unknown }).tile;
  if (status === 201 && isObj(tile) && typeof tile.id === 'string' && typeof tile.segment === 'string' && typeof tile.slug === 'string') {
    return { kind: 'ok', tile: { id: tile.id, segment: tile.segment, slug: tile.slug } };
  }
  return refusedOr(body);
}

/** Unpin a tile from the DM's screen. */
export async function unpinTile(campaignId: string, tileId: string, fetchImpl: typeof fetch = fetch): Promise<DoneAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/dm-screen/${enc(tileId)}`, { method: 'DELETE' }, fetchImpl);
  return status === 204 ? { kind: 'ok' } : refusedOr(body);
}

/** A kind's tile facts: what may be shown, the system's, and the DM's own. */
export async function readTileFacts(campaignId: string, segment: string, fetchImpl: typeof fetch = fetch): Promise<FactsAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/dm-screen/facts?segment=${enc(segment)}`, {}, fetchImpl);
  const b = (body ?? {}) as Record<string, unknown>;
  if (status === 200 && typeof b.family === 'string' && Array.isArray(b.available) && isKeys(b.system) && (b.mine === null || isKeys(b.mine))) {
    const available = b.available.filter((a): a is { key: string; label: string } => isObj(a) && typeof a.key === 'string' && typeof a.label === 'string');
    return { kind: 'ok', choice: { segment, family: b.family, available, system: b.system, mine: b.mine, max: typeof b.max === 'number' ? b.max : 10 } };
  }
  return refusedOr(body);
}

/** Save the DM's own facts for a kind, in order; an empty list goes back to the system's. */
export async function saveTileFacts(campaignId: string, segment: string, facts: string[], fetchImpl: typeof fetch = fetch): Promise<FactsSaved> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/dm-screen/facts`, { method: 'PUT', body: JSON.stringify({ segment, facts }) }, fetchImpl);
  const mine = ((body ?? {}) as { mine?: unknown }).mine;
  if (status === 200 && (mine === null || isKeys(mine))) return { kind: 'ok', mine };
  return refusedOr(body);
}
